import { fetchJson } from "../lib/api";

export type SourceCoverageCounts = {
  total: number | string | null;
  complete: number | string | null;
  review_needed: number | string | null;
  source_gap: number | string | null;
  unsupported: number | string | null;
  unknown: number | string | null;
  with_pdf_evidence: number | string | null;
  with_ugl_evidence: number | string | null;
  with_datanorm_evidence: number | string | null;
  with_manual_evidence: number | string | null;
};

export type SourceCoverageDomainSummary = SourceCoverageCounts & {
  domain: "article" | "order" | "cost_center" | string;
  label: string;
  href: string;
};

export type SourceCoverageSummaryResponse = {
  customer_id: string | null;
  summary: SourceCoverageCounts;
  domains: SourceCoverageDomainSummary[];
};

export async function fetchSourceCoverageSummary(): Promise<SourceCoverageSummaryResponse> {
  return fetchJson<SourceCoverageSummaryResponse>("/api/source-coverage/summary");
}

export type SourceFileFormat = "pdf" | "ugl" | "datanorm" | "manual";
export type SourceFileParserStatus =
  | "used"
  | "unused"
  | "not_parsed"
  | "not_applicable";
export type SourceFileSortBy =
  | "source_date"
  | "imported_at"
  | "format"
  | "parser_status"
  | "status";
export type SourceFileSortDir = "asc" | "desc";

export type SourceFileFormatSummary = {
  format: SourceFileFormat | string;
  label: string;
  file_count: number | string | null;
  last_import_at: string | null;
};

export type SourceFileParserStatusSummary = {
  status: SourceFileParserStatus | string;
  label: string;
  file_count: number | string | null;
};

export type SourceFileInventorySummary = {
  total_file_count: number | string | null;
  last_import_at: string | null;
  formats: SourceFileFormatSummary[];
  parser_statuses?: SourceFileParserStatusSummary[];
};

export type SourceFileRow = {
  file_ref: string;
  filename: string;
  format: SourceFileFormat | string;
  doc_type: string | null;
  supplier: string | null;
  status_scope: string | null;
  batch_ref: string | null;
  batch_file_count: number | string | null;
  source_date_from: string | null;
  source_date_to: string | null;
  imported_at: string | null;
  size_bytes: number | string | null;
  status: string | null;
  status_reason: string | null;
  status_updated_at: string | null;
  document_count: number | string | null;
  line_count: number | string | null;
  parser_status: SourceFileParserStatus | string;
  parser_status_reason: string | null;
  /** Canonical document reference for tokenized PDF actions. */
  pdf_doc_id?: string | null;
};

export type SourceFileInventoryResponse = {
  customer_id: string | null;
  summary: SourceFileInventorySummary;
  total: number;
  limit: number;
  offset: number;
  items: SourceFileRow[];
};

export type SourceReadModelStatus = {
  pending: boolean;
  pending_refresh_scope: "import" | "full" | string | null;
  latest_source_event_at: string | null;
  last_refresh_at: string | null;
  pending_event_count: number | string | null;
  pending_wholesale_event_count: number | string | null;
  source_event_count: number | string | null;
  excluded_file_count: number | string | null;
  excluded_wholesale_batch_count: number | string | null;
};

export type SourceReadModelRefreshResponse = {
  status: SourceReadModelStatus;
  refreshed: boolean;
  scope: string;
  row_counts: Record<string, number | string | null>;
  diagnostics: Record<string, number | string | null>;
  consistency: Record<string, number | string | boolean | null>;
};

export type SourceFileStatusHistoryEntry = {
  action: string;
  reason: string | null;
  created_at: string | null;
};

export type SourceFileStatusHistoryResponse = {
  file_ref: string;
  events: SourceFileStatusHistoryEntry[];
};

export async function fetchSourceFileInventory(params?: {
  format?: string;
  docType?: string;
  supplier?: string;
  parserStatus?: string;
  sourceDateFrom?: string;
  sourceDateTo?: string;
  sortBy?: SourceFileSortBy;
  sortDir?: SourceFileSortDir;
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<SourceFileInventoryResponse> {
  const search = new URLSearchParams();
  if (params?.format && params.format !== "all") search.set("format", params.format);
  if (params?.docType && params.docType !== "all") search.set("doc_type", params.docType);
  if (params?.supplier && params.supplier !== "all") search.set("supplier", params.supplier);
  if (params?.parserStatus && params.parserStatus !== "all") {
    search.set("parser_status", params.parserStatus);
  }
  if (params?.sourceDateFrom) search.set("source_date_from", params.sourceDateFrom);
  if (params?.sourceDateTo) search.set("source_date_to", params.sourceDateTo);
  if (params?.sortBy) search.set("sort_by", params.sortBy);
  if (params?.sortDir) search.set("sort_dir", params.sortDir);
  if (params?.q) search.set("q", params.q);
  if (params?.limit != null) search.set("limit", String(params.limit));
  if (params?.offset != null) search.set("offset", String(params.offset));
  const query = search.toString();
  return fetchJson<SourceFileInventoryResponse>(
    `/api/source-coverage/files${query ? `?${query}` : ""}`,
  );
}

export async function fetchSourceFileDetail(
  fileRef: string,
): Promise<SourceFileRow> {
  return fetchJson<SourceFileRow>(
    `/api/source-coverage/files/${encodeURIComponent(fileRef)}`,
  );
}

export async function fetchSourceReadModelStatus(): Promise<SourceReadModelStatus> {
  return fetchJson<SourceReadModelStatus>("/api/source-coverage/read-model/status");
}

export async function refreshSourceReadModel(): Promise<SourceReadModelRefreshResponse> {
  return fetchJson<SourceReadModelRefreshResponse>(
    "/api/source-coverage/read-model/refresh",
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}

export async function fetchSourceFileStatusHistory(
  fileRef: string,
): Promise<SourceFileStatusHistoryResponse> {
  return fetchJson<SourceFileStatusHistoryResponse>(
    `/api/source-coverage/files/${encodeURIComponent(fileRef)}/history`,
  );
}

export async function excludeSourceFile(
  fileRef: string,
  reason?: string | null,
): Promise<SourceFileRow> {
  return updateSourceFileStatus(fileRef, "exclude", reason);
}

export async function restoreSourceFile(
  fileRef: string,
  reason?: string | null,
): Promise<SourceFileRow> {
  return updateSourceFileStatus(fileRef, "restore", reason);
}

async function updateSourceFileStatus(
  fileRef: string,
  action: "exclude" | "restore",
  reason?: string | null,
): Promise<SourceFileRow> {
  return fetchJson<SourceFileRow>(
    `/api/source-coverage/files/${encodeURIComponent(fileRef)}/${action}`,
    {
      method: "POST",
      body: JSON.stringify({ reason: reason || null }),
    },
  );
}
